import './Header.scss';

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../ivydark.png';
import useScrollSpy from '../hooks/useScrollSpy';
import { Clock, Phone } from './Icons';

type SectionLink = { id: string; text: string; to: string };
type PageLink = { to: string; text: string };
type HeaderProps = { links: SectionLink[]; pages?: PageLink[] };

export const scrollToSection = (sectionId: string) => {
  const section = document.getElementById(sectionId);
  if (section) section.scrollIntoView({ behavior: 'smooth' });
};

export const Header: React.FC<HeaderProps> = ({ links, pages = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === '/';
  const sectionIds = useMemo(() => links.map((l) => l.id), [links]);
  const activeSection = useScrollSpy(sectionIds, 120);
  const [open, setOpen] = useState(false);

  // Keep the URL in step with the section on screen without touching the router (no re-render, no scroll fight).
  // Near the top of the page the spy can still hold a stale section from before navigation, so treat it as home.
  useEffect(() => {
    if (!onHome || !activeSection) return;
    const atTop = window.scrollY < 300 || activeSection === 'home';
    window.history.replaceState(null, '', atTop ? '/' : '/#' + activeSection);
  }, [activeSection, onHome]);

  useEffect(() => {
    if (!open) return;
    document.body.classList.add('no-scroll');
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('no-scroll');
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /**
   * Links point at the real pages (/services, /about…) so crawlers find them. On the homepage a click
   * scrolls to the matching section instead, keeping the one-page feel; elsewhere it opens the page.
   */
  const go = (id: string, to: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(false);
    if (onHome && document.getElementById(id)) scrollToSection(id);
    else {
      navigate(to);
      window.scrollTo({ top: 0 });
    }
  };
  const isActive = (l: SectionLink) => (onHome ? activeSection === l.id : location.pathname === l.to);

  return (
    <header className={`header${open ? ' is-open' : ''}`}>
      <div className="topbar">
        <div className="container topbar__inner">
          <span className="topbar__note">
            <Clock />
            <span className="topbar__long">Free on-site quotes across Delta &amp; the Lower Mainland</span>
            <span className="topbar__short">Free on-site quotes in Delta</span>
          </span>
          <a className="topbar__pill" href="tel:+16043655225">
            Call (604) 365-5225
          </a>
        </div>
      </div>

      <div className="header__bar">
        <div className="container header__inner">
          <a className="header__logo" href="/" onClick={go('home', '/')} aria-label="Ivy League Painting — home">
            <img src={logo} alt="Ivy League Painting Ltd." />
          </a>

          <nav className="header__nav" aria-label="Primary">
            {links.map((l) => (
              <a key={l.id} href={l.to} onClick={go(l.id, l.to)} className={isActive(l) ? 'is-active' : ''}>
                {l.text}
              </a>
            ))}
            {pages.map((p) => (
              <Link key={p.to} to={p.to} onClick={() => setOpen(false)} className={location.pathname === p.to ? 'is-active' : ''}>
                {p.text}
              </Link>
            ))}
          </nav>

          <div className="header__actions">
            <a className="header__phone" href="tel:+16043655225" aria-label="Call (604) 365-5225">
              <Phone />
              <span>(604) 365-5225</span>
            </a>
            <a className="btn btn--blue btn--sm header__cta" href="/contact" onClick={go('contact', '/contact')}>
              Free quote
            </a>
            <button
              className="header__burger"
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
            >
              <span />
              <span />
            </button>
          </div>
        </div>
      </div>

      <div className="header__panel" aria-hidden={!open}>
        <nav aria-label="Mobile">
          {links.map((l, i) => (
            <a key={l.id} href={l.to} onClick={go(l.id, l.to)} style={{ '--i': i } as React.CSSProperties}>
              {l.text}
            </a>
          ))}
          {pages.map((p, i) => (
            <Link key={p.to} to={p.to} onClick={() => setOpen(false)} style={{ '--i': links.length + i } as React.CSSProperties}>
              {p.text}
            </Link>
          ))}
        </nav>
        <div className="header__panel-foot">
          <a className="btn btn--ghost btn--block" href="tel:+16043655225">
            <Phone /> (604) 365-5225
          </a>
          <a className="btn btn--blue btn--block" href="/contact" onClick={go('contact', '/contact')}>
            Get a free quote
          </a>
        </div>
      </div>
    </header>
  );
};
